import { useEffect, useMemo, useState } from 'react';
import { customFetcherRaw } from './customFetcher';

type CacheEntry = {
    objectUrl: string;
    refCount: number;
};

const imageCache = new Map<string, CacheEntry>();

const normalizeToApiEndpoint = (url: string): string | null => {
    const trimmed = url.trim();
    if (!trimmed) {
        return null;
    }
    if (trimmed.startsWith('blob:') || trimmed.startsWith('data:')) {
        return null;
    }

    const parsedUrl = new URL(trimmed, window.location.origin);
    const pathWithSearch = `${parsedUrl.pathname}${parsedUrl.search}`;

    if (pathWithSearch.startsWith('/api/')) {
        return pathWithSearch.replace('/api', '');
    }

    if (pathWithSearch.startsWith('/')) {
        return pathWithSearch;
    }

    return null;
};

export const fetchAuthenticatedBlobByUrl = async (url: string): Promise<Blob> => {
    const endpoint = normalizeToApiEndpoint(url);

    if (endpoint) {
        const response = await customFetcherRaw({
            endpoint,
            method: 'GET',
        });
        return response.blob();
    }

    const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
    });

    if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    return response.blob();
};

const fetchAndCacheImage = async (endpoint: string, signal: AbortSignal): Promise<string> => {
    const cached = imageCache.get(endpoint);
    if (cached) {
        return cached.objectUrl;
    }

    const response = await customFetcherRaw({
        endpoint,
        method: 'GET',
        signal,
    });

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    imageCache.set(endpoint, {
        objectUrl,
        refCount: 0,
    });

    return objectUrl;
};

const retainCachedImage = (endpoint: string): string | null => {
    const entry = imageCache.get(endpoint);
    if (!entry) {
        return null;
    }
    entry.refCount += 1;
    return entry.objectUrl;
};

const releaseCachedImage = (endpoint: string): void => {
    const entry = imageCache.get(endpoint);
    if (!entry) {
        return;
    }

    entry.refCount -= 1;
    if (entry.refCount <= 0) {
        URL.revokeObjectURL(entry.objectUrl);
        imageCache.delete(endpoint);
    }
};

type AuthenticatedImageResult = {
    src: string | undefined;
    isLoading: boolean;
};

export const useAuthenticatedImage = (url: string | null | undefined): AuthenticatedImageResult => {
    const [src, setSrc] = useState<string | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const normalizedEndpoint = useMemo(() => {
        if (!url) {
            return null;
        }
        return normalizeToApiEndpoint(url);
    }, [url]);

    useEffect(() => {
        if (!url) {
            setSrc(undefined);
            setIsLoading(false);
            return;
        }

        if (!normalizedEndpoint) {
            setSrc(url);
            setIsLoading(false);
            return;
        }

        const abortController = new AbortController();
        let retainedEndpoint: string | null = null;

        const cachedSrc = retainCachedImage(normalizedEndpoint);
        if (cachedSrc) {
            retainedEndpoint = normalizedEndpoint;
            setSrc(cachedSrc);
            setIsLoading(false);
        } else {
            setSrc(undefined);
            setIsLoading(true);

            void fetchAndCacheImage(normalizedEndpoint, abortController.signal)
                .then((resolvedSrc) => {
                    if (abortController.signal.aborted) {
                        const staleEntry = imageCache.get(normalizedEndpoint);
                        if (staleEntry && staleEntry.refCount <= 0) {
                            URL.revokeObjectURL(staleEntry.objectUrl);
                            imageCache.delete(normalizedEndpoint);
                        }
                        return;
                    }

                    const retainedSrc = retainCachedImage(normalizedEndpoint);
                    if (!retainedSrc) {
                        setSrc(undefined);
                        setIsLoading(false);
                        return;
                    }

                    retainedEndpoint = normalizedEndpoint;
                    setSrc(resolvedSrc);
                    setIsLoading(false);
                })
                .catch(() => {
                    if (abortController.signal.aborted) {
                        return;
                    }
                    setSrc(undefined);
                    setIsLoading(false);
                });
        }

        return () => {
            abortController.abort();
            if (retainedEndpoint) {
                releaseCachedImage(retainedEndpoint);
            }
        };
    }, [normalizedEndpoint, url]);

    return { src, isLoading };
};
